// @flow

import * as React from 'react';
import { createRefetchContainer, commitMutation, graphql } from 'react-relay/compat';
import classNames from 'classnames';
import Favorite from 'app/components/icons/Favorite';
import Emojify from 'app/components/shared/Emojify';
import Dropdown from 'app/components/shared/Dropdown';
import Icon from 'app/components/shared/Icon';
import PipelineStatus from 'app/components/shared/PipelineStatus';
import permissions from 'app/lib/permissions';
import CableStore from 'app/stores/CableStore';
import Environment from 'app/lib/relay/environment';
import Status from './Status';
import Metrics from './Metrics';
import Graph from './Graph';
import BranchSettingsDialog from './BranchSettingsDialog';
import type { RelayRefetchProp } from 'react-relay';
import type { Pipeline_pipeline } from './__generated__/Pipeline_pipeline.graphql';

type Props = {
  pipeline: Pipeline_pipeline,
  includeGraphData: boolean,
  relay: RelayRefetchProp
};

type State = {
  showingMenu: boolean,
  showingBranchSettingsDialog: boolean
};

class Pipeline extends React.Component<Props, State> {
  state = {
    showingMenu: false,
    showingBranchSettingsDialog: false
  };

  actionsDropdown: ?Dropdown;

  componentDidMount() {
    CableStore.on("websocket:event", this.handleWebsocketEvent);
  }

  componentWillUnmount() {
    CableStore.off("websocket:event", this.handleWebsocketEvent);
  }

  render() {
    return (
      <div
        data-testid="pipeline"
        data-pipeline={this.props.pipeline.uuid}
        className="flex items-stretch border border-gray rounded px2 mb2"
        style={{ height: 82 }}
      >
        <div className="flex flex-none items-center pl2 pr2">
          <Status pipeline={this.props.pipeline} />
        </div>

        <a href={this.props.pipeline.url} className="flex flex-auto items-center px2 text-decoration-none color-inherit mr3">
          <div className="truncate">
            <div className="flex items-center">
              <h2 data-testid="pipeline__name" className="inline h3 regular m0 line-height-2">
                <Emojify text={this.props.pipeline.name} />
              </h2>
              <PipelineStatus public={this.props.pipeline.public} />
            </div>
            {this.renderDescription()}
          </div>
        </a>

        {this.renderGraph()}

        <div className="flex flex-none items-center justify-start xs-hide ml2">
          <Metrics pipeline={this.props.pipeline} />
        </div>

        {this.renderActions()}
      </div>
    );
  }

  renderDescription() {
    if (this.props.pipeline.description) {
      return (
        <div data-testid="pipeline__description" className="truncate dark-gray" style={{ marginTop: 3 }}>
          <Emojify className="h4 regular" text={this.props.pipeline.description} />
        </div>
      );
    }
  }

  renderGraph() {
    // Toggle between showing the graph, or showing a placeholder until the
    // data is finally loaded in.
    let graph;
    if (this.props.includeGraphData) {
      graph = (
        <div>
          <div className="h6 regular dark-gray mb1 line-height-1">{this.props.pipeline.defaultBranch || "All Builds"}</div>
          <Graph pipeline={this.props.pipeline} />
        </div>
      );
    } else {
      graph = (
        <div style={{ width: 239 }}>
          <div className="h6 regular dark-gray mb1 line-height-1">{this.props.pipeline.defaultBranch || "All Builds"}</div>
          <div style={{ height: 35 }} className="flex">
            <div style={{ height: 3, width: "100%", marginTop: "auto" }} className="bg-gray animation-loading-bar" />
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center flex-none ml3 xs-hide sm-hide pr4">
        {graph}
      </div>
    );
  }

  renderActions() {
    // Make sure we're allowed to favorite this pipeline
    const favoriteButton = permissions(this.props.pipeline.permissions).check(
      {
        allowed: "pipelineFavorite",
        render: () => {
          return (
            <button key="favorite" className="btn p0 dark-gray line-height-1" onClick={this.handleFavoriteClick}>
              <Favorite favorite={this.props.pipeline.favorite} />
            </button>
          );
        }
      }
    );

    // Make sure we can perform the actions inside the dropdown
    const dropdownActions = permissions(this.props.pipeline.permissions).collect(
      {
        always: true,
        render: () => {
          return (
            <a key="builds-view" href={`${this.props.pipeline.url}/builds`} className="btn block hover-lime">
              View Builds
            </a>
          );
        }
      },
      {
        allowed: "pipelineUpdate",
        render: () => {
          return (
            <button key="branch-settings" className="btn block hover-lime" onClick={this.handleBranchSettingsShow}>
              Branch Settings
            </button>
          );
        }
      }
    );

    // Only render the dropdown button if there's something to put inside it
    let dropdownButton;
    if (dropdownActions.length > 0) {
      dropdownButton = (
        <Dropdown key="dropdown" align="center" width={150} onToggle={this.handleMenuToggle} ref={(actionsDropdown) => this.actionsDropdown = actionsDropdown}>
          <button className="btn p0 gray hover-dark-gray no-title-tooltip" aria-label="More Actions">
            <Icon icon="menu" className={classNames({ "dark-gray": this.state.showingMenu })} />
          </button>
          {dropdownActions}
        </Dropdown>
      );
    }

    let branchSettingsDialog;
    if (this.props.pipeline.permissions.pipelineUpdate.allowed) {
      branchSettingsDialog = (
        <BranchSettingsDialog
          isOpen={this.state.showingBranchSettingsDialog}
          onRequestClose={this.handleBranchSettingsRequestClose}
          pipeline={this.props.pipeline}
        />
      );
    }

    return (
      <div className="flex flex-none flex-column justify-center ml-auto pr2">
        {favoriteButton}
        {(favoriteButton && dropdownButton) && <div className="mb1" />}
        {dropdownButton}
        {branchSettingsDialog}
      </div>
    );
  }

  handleWebsocketEvent = (payload) => {
    if (payload.subevent === "project:updated" && payload.graphql.id === this.props.pipeline.id) {
      const { pipeline: { id }, includeGraphData } = this.props;
      this.props.relay.refetch({ id, includeGraphData });
    }
  };

  handleBranchSettingsShow = () => {
    if (this.state.showingMenu && this.actionsDropdown) {
      this.actionsDropdown.setShowing(false);
      this.setState({ showingMenu: false });
    }

    this.setState({ showingBranchSettingsDialog: true });
  };

  handleBranchSettingsRequestClose = () => {
    this.setState({ showingBranchSettingsDialog: false });
  };

  handleMenuToggle = (visible) => {
    this.setState({ showingMenu: visible });
  };

  handleFavoriteClick = () => {
    const environment = Environment.get();
    const { pipeline } = this.props;
    const input = { id: pipeline.id, favorite: !pipeline.favorite };

    commitMutation(environment, {
      mutation: graphql`
        mutation PipelineFavoriteMutation($input: PipelineFavoriteInput!) {
          pipelineFavorite(input: $input) {
            pipeline {
              favorite
            }
          }
        }
      `,
      variables: { input },
      optimisticResponse: { pipelineFavorite: { pipeline: input } }
    });
  }
}

export default createRefetchContainer(
  Pipeline,
  graphql`
    fragment Pipeline_pipeline on Pipeline @argumentDefinitions(
      includeGraphData: {type: "Boolean!"}
    ) {
      ...Status_pipeline
      ...Metrics_pipeline
      ...BranchSettingsDialog_pipeline
      ...Graph_pipeline @arguments(includeGraphData: $includeGraphData)
      id
      uuid
      name
      slug
      public
      description
      defaultBranch
      url
      favorite
      permissions {
        pipelineFavorite {
          allowed
        }
        pipelineUpdate {
          allowed
        }
      }
    }
  `,
  graphql`
    query PipelineRefetchQuery($id: ID!, $includeGraphData: Boolean!) {
      node(id: $id) {
        ...Pipeline_pipeline @arguments(includeGraphData: $includeGraphData)
      }
    }
  `
);
